import React, { useEffect, useState } from 'react';
import { Badge, Button, Col, Modal, Row, Spinner } from 'react-bootstrap';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { cashVoucherAPI } from '../../services';
import {
  CATEGORY_LABELS,
  PAYMENT_METHOD_LABELS,
  VOUCHER_STATUS_COLORS,
  VOUCHER_STATUS_LABELS,
} from '../../utils/cashierConstants';

const formatCurrency = (value) =>
  new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 }).format(
    Number(value || 0),
  );

const formatDateTime = (value) => {
  if (!value) return '-';
  try { return format(new Date(value), 'dd/MM/yyyy HH:mm'); } catch { return value; }
};

const InfoRow = ({ label, value }) => (
  <div className="d-flex justify-content-between py-1 border-bottom small">
    <span className="text-muted">{label}</span>
    <span className="fw-semibold text-end" style={{ maxWidth: '60%' }}>{value || '-'}</span>
  </div>
);

const VoucherDetailModal = ({ voucherId, show, onHide }) => {
  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(false);
  const [zoomedImg, setZoomedImg] = useState(null);

  useEffect(() => {
    if (!show || !voucherId) return;
    setVoucher(null);
    setLoading(true);
    cashVoucherAPI
      .getCashVoucherById(voucherId)
      .then((res) => setVoucher(res))
      .catch((err) => toast.error(err?.message || 'Không tải được chi tiết phiếu chi'))
      .finally(() => setLoading(false));
  }, [show, voucherId]);

  const evidenceUrls = Array.isArray(voucher?.evidence_urls)
    ? voucher.evidence_urls
    : [];

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Chi tiết phiếu chi</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" size="sm" /> Đang tải...
            </div>
          ) : !voucher ? (
            <div className="text-muted text-center py-4">Không có dữ liệu</div>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <h5 className="mb-0">
                    {voucher.voucher_number || voucher.id}
                    {voucher.is_backdated && (
                      <Badge bg="info" text="dark" className="ms-2 align-middle">LÙI CA</Badge>
                    )}
                  </h5>
                  <small className="text-muted">{formatDateTime(voucher.created_at || voucher.createdAt)}</small>
                </div>
                <Badge bg={VOUCHER_STATUS_COLORS[voucher.status] || 'secondary'} className="fs-6">
                  {VOUCHER_STATUS_LABELS[voucher.status] || voucher.status}
                </Badge>
              </div>

              {/* #110729 — phiếu chi LÙI CA: nêu rõ ca cũ + không trừ két. */}
              {voucher.is_backdated && (
                <div className="mb-3 p-2 border border-info rounded bg-light">
                  <div className="text-info small fw-semibold mb-1">
                    🔁 Phiếu chi LÙI CA — không trừ két lần hai
                  </div>
                  <div className="d-flex justify-content-between small">
                    <span className="text-muted">Ca cũ:</span>
                    <span className="fw-semibold">
                      {voucher.shift?.employee?.full_name || '—'}
                      {voucher.shift?.start_time && (
                        <> · {formatDateTime(voucher.shift.start_time)}</>
                      )}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between small">
                    <span className="text-muted">Khoản lệch nguồn:</span>
                    <span>{voucher.variance_ref_id || '—'}</span>
                  </div>
                </div>
              )}

              <Row className="g-0 mb-3">
                <Col md={6}>
                  <InfoRow label="Chi nhánh" value={voucher.branch?.name} />
                  <InfoRow label="Số tiền" value={formatCurrency(voucher.amount)} />
                  <InfoRow label="Danh mục" value={CATEGORY_LABELS[voucher.category] || voucher.category} />
                  <InfoRow label="Phương thức" value={PAYMENT_METHOD_LABELS[voucher.payment_method] || voucher.payment_method} />
                  <InfoRow label="Ngày chi" value={formatDateTime(voucher.voucher_date)} />
                </Col>
                <Col md={6} className="ps-md-3">
                  <InfoRow label="Người nhận" value={voucher.recipient_name} />
                  <InfoRow label="SĐT người nhận" value={voucher.recipient_phone} />
                  <InfoRow label="Người tạo" value={voucher.creator?.full_name} />
                  <InfoRow label="Người duyệt" value={voucher.approver?.full_name} />
                  <InfoRow label="Thời gian duyệt" value={formatDateTime(voucher.approved_at)} />
                </Col>
              </Row>

              {voucher.description && (
                <div className="mb-3">
                  <div className="text-muted small mb-1">Mô tả</div>
                  <p className="mb-0 small">{voucher.description}</p>
                </div>
              )}

              {voucher.rejected_reason && (
                <div className="mb-3 p-2 border border-danger rounded">
                  <div className="text-danger small fw-semibold mb-1">Lý do từ chối</div>
                  <p className="mb-0 small">{voucher.rejected_reason}</p>
                </div>
              )}

              {/* #164550 — khối "Đã huỷ bởi … lúc … — Lý do: …" (AC16). */}
              {voucher.status === 'CANCELLED' && (
                <div className="mb-3 p-2 border border-secondary rounded bg-light">
                  <div className="text-secondary small fw-semibold mb-1">
                    🚫 Đã huỷ bởi {voucher.canceller?.full_name || '—'} lúc{' '}
                    {formatDateTime(voucher.cancelled_at)}
                  </div>
                  {voucher.cancel_reason && (
                    <p className="mb-0 small">Lý do: {voucher.cancel_reason}</p>
                  )}
                </div>
              )}

              {voucher.import_request && (
                <div className="mb-3 p-2 border border-info rounded bg-light">
                  <div className="text-info small fw-semibold mb-1">📦 Phiếu nhập kho liên kết</div>
                  <div className="d-flex justify-content-between small">
                    <span className="text-muted">Số phiếu:</span>
                    <span className="fw-semibold">{voucher.import_request.request_number}</span>
                  </div>
                  <div className="d-flex justify-content-between small">
                    <span className="text-muted">Trạng thái:</span>
                    <Badge bg={
                      voucher.import_request.status === 'APPROVED' ? 'success' :
                      voucher.import_request.status === 'REJECTED' ? 'danger' :
                      'warning'
                    }>
                      {voucher.import_request.status === 'APPROVED' ? 'Đã duyệt' :
                       voucher.import_request.status === 'REJECTED' ? 'Bị từ chối' :
                       voucher.import_request.status === 'PENDING' ? 'Chờ duyệt' :
                       voucher.import_request.status}
                    </Badge>
                  </div>
                  {voucher.import_request.supplier?.name && (
                    <div className="d-flex justify-content-between small">
                      <span className="text-muted">NCC:</span>
                      <span>{voucher.import_request.supplier.name}</span>
                    </div>
                  )}
                  {voucher.import_request.invoice_number && (
                    <div className="d-flex justify-content-between small">
                      <span className="text-muted">Số HĐ:</span>
                      <span>{voucher.import_request.invoice_number}</span>
                    </div>
                  )}
                </div>
              )}

              {/* #201201 — phiếu kiểm kho nhập hàng gắn với phiếu chi này (lệnh sếp
                  update_id=731599485, 02/09/2026). Chỉ là tham chiếu chứng từ: tồn kho đã tăng
                  từ lúc phiếu kiểm hoàn tất, không cộng thêm lần nào vì phiếu chi. */}
              {voucher.goods_receipt && (
                <div className="mb-3 p-2 border border-primary rounded bg-light">
                  <div className="text-primary small fw-semibold mb-1">🧾 Phiếu kiểm kho nhập hàng liên kết</div>
                  <div className="d-flex justify-content-between small">
                    <span className="text-muted">Số phiếu:</span>
                    <span className="fw-semibold">{voucher.goods_receipt.receipt_number}</span>
                  </div>
                  <div className="d-flex justify-content-between small">
                    <span className="text-muted">Trạng thái:</span>
                    <Badge bg={
                      voucher.goods_receipt.status === 'COMPLETED' ? 'success' :
                      voucher.goods_receipt.status === 'PENDING_REVIEW' ? 'warning' :
                      'secondary'
                    }>
                      {voucher.goods_receipt.status === 'COMPLETED' ? 'Đã nhập kho' :
                       voucher.goods_receipt.status === 'PENDING_REVIEW' ? 'Chờ duyệt chênh lệch' :
                       voucher.goods_receipt.status === 'IN_PROGRESS' ? 'Đang kiểm' :
                       voucher.goods_receipt.status}
                    </Badge>
                  </div>
                  {voucher.goods_receipt.purchase_order?.order_number && (
                    <div className="d-flex justify-content-between small">
                      <span className="text-muted">Đơn đặt hàng:</span>
                      <span>{voucher.goods_receipt.purchase_order.order_number}</span>
                    </div>
                  )}
                  {voucher.goods_receipt.has_variance && (
                    <div className="d-flex justify-content-between small">
                      <span className="text-muted">Chênh lệch:</span>
                      <span className="text-warning fw-semibold">Có lệch số lượng</span>
                    </div>
                  )}
                  {voucher.goods_receipt.purchase_order?.id && (
                    <a
                      className="small"
                      href={`/inventory/purchase-order/${voucher.goods_receipt.purchase_order.id}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Xem chi tiết phiếu kiểm →
                    </a>
                  )}
                </div>
              )}

              {evidenceUrls.length > 0 && (
                <div>
                  <div className="text-muted small mb-2">Chứng từ ({evidenceUrls.length} ảnh)</div>
                  <div className="d-flex flex-wrap gap-2">
                    {evidenceUrls.map((url, idx) => (
                      <img
                        key={idx}
                        src={url}
                        alt={`Chứng từ ${idx + 1}`}
                        style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, cursor: 'zoom-in', border: '1px solid #dee2e6' }}
                        onClick={() => setZoomedImg(url)}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Đóng</Button>
        </Modal.Footer>
      </Modal>

      {zoomedImg && (
        <Modal show centered size="xl" onHide={() => setZoomedImg(null)}>
          <Modal.Header closeButton />
          <Modal.Body className="p-0 text-center bg-dark">
            <img
              src={zoomedImg}
              alt="Chứng từ"
              style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
            />
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

export default VoucherDetailModal;
